import { useEffect } from 'react';

export interface SEOOptions {
    title?: string;
    description?: string;
    keywords?: string[];
    ogType?: 'website' | 'article' | 'product';
    ogImage?: string;
    canonicalUrl?: string;
}

const DEFAULT_TITLE = 'Цветочный магазин «Ваниль» — цветы и букеты в Булгаково и Уфе';
const DEFAULT_DESCRIPTION = 'Свежие цветы, готовые и авторские букеты с доставкой по Уфе и Булгаково. Сборка по фото за 2 часа, гарантия свежести.';
const DEFAULT_IMAGE = 'https://vanilufa.ru/og-image.jpg';
const BASE_URL = 'https://vanilufa.ru';

export function useSEO({
    title = DEFAULT_TITLE,
    description = DEFAULT_DESCRIPTION,
    keywords,
    ogType = 'website',
    ogImage = DEFAULT_IMAGE,
    canonicalUrl
}: SEOOptions) {
    useEffect(() => {
        // 1. Update Document Title
        document.title = title;

        // Helper to update or append <meta>
        const setMetaTag = (attrName: 'name' | 'property', attrValue: string, content: string) => {
            if (!content) return;
            let element = document.querySelector(`meta[${attrName}="${attrValue}"]`) as HTMLMetaElement | null;
            if (!element) {
                element = document.createElement('meta');
                element.setAttribute(attrName, attrValue);
                document.head.appendChild(element);
            }
            element.setAttribute('content', content);
        };

        // Helper to update or append <link>
        const setLinkTag = (rel: string, href: string) => {
            if (!href) return;
            let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
            if (!element) {
                element = document.createElement('link');
                element.setAttribute('rel', rel);
                document.head.appendChild(element);
            }
            element.setAttribute('href', href);
        };

        const currentUrl = canonicalUrl || (typeof window !== 'undefined' ? window.location.href : BASE_URL);

        // Standard Meta
        setMetaTag('name', 'description', description);
        if (keywords && keywords.length > 0) {
            setMetaTag('name', 'keywords', keywords.join(', '));
        }

        // Canonical URL
        setLinkTag('canonical', currentUrl);

        // Open Graph (Facebook, Telegram, WhatsApp, Viber, LinkedIn, etc.)
        setMetaTag('property', 'og:type', ogType);
        setMetaTag('property', 'og:site_name', 'Цветочный магазин «Ваниль»');
        setMetaTag('property', 'og:locale', 'ru_RU');
        setMetaTag('property', 'og:title', title);
        setMetaTag('property', 'og:description', description);
        setMetaTag('property', 'og:url', currentUrl);
        setMetaTag('property', 'og:image', ogImage);
        setMetaTag('property', 'og:image:secure_url', ogImage);
        setMetaTag('property', 'og:image:type', 'image/jpeg');
        setMetaTag('property', 'og:image:width', '1200');
        setMetaTag('property', 'og:image:height', '630');
        setMetaTag('property', 'og:image:alt', title);

        // Twitter Cards
        setMetaTag('name', 'twitter:card', 'summary_large_image');
        setMetaTag('name', 'twitter:title', title);
        setMetaTag('name', 'twitter:description', description);
        setMetaTag('name', 'twitter:image', ogImage);
        setMetaTag('name', 'twitter:image:alt', title);

        // VKontakte
        setMetaTag('property', 'vk:image', ogImage);
    }, [title, description, keywords, ogType, ogImage, canonicalUrl]);
}
